import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'
import { Psychology, Search, Refresh } from '@mui/icons-material'
import { TextField, InputAdornment, FormControl, Select, MenuItem, IconButton, Autocomplete, Checkbox as MuiCheckbox, ToggleButton, ToggleButtonGroup } from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'

// Third-party Imports
import { rankItem } from '@tanstack/match-sorter-utils'

// Component Imports
import CampaignPreparationTable from '../organisms/CampaignPreparationTable'
import CampaignProgressBar from '../../../../../campaigns/components/CampaignProgressBar'

import { getCampaignFirmsAndState, getCampaign, updateCampaignFirmSelection, approveCampaignFirmSelection, downloadCampaignFirmsExcel, abortSelectFirms } from '../../../services/B2BServices'
import TitleFilterDialog from '../molecules/TitleFilterDialog'
import { getUrlParams } from '../../../services/urlServices'
import { useToast } from '../../../../../campaigns/hooks/useToast'
import { useGlobalStoreActions } from '../../../../../store/globalStore'
import TableViewIcon from '@mui/icons-material/Download'

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)

  addMeta({
    itemRank
  })

  return itemRank.passed
}

const DEFAULT_PAGE = 0
const DEFAULT_PAGE_SIZE = 10
const DEFAULT_TOTAL_ELEMENTS = 0
const DEFAULT_TOTAL_PAGE_COUNT = 0

const SORT_FIELD_MAP = {
  name: 'firmName'
}

const buildSortingParams = (sorting) => {
  const sortBy = SORT_FIELD_MAP[sorting?.[0]?.id]

  if (!sortBy) return {}

  return { sortBy, direction: sorting[0].desc ? 'DESC' : 'ASC' }
}

const CampaignPreparation = ({ onCampaignUpdate, campaign, showProgressBar = false }) => {
  const { t } = useTranslation()
  const queryParams = getUrlParams()

  const { showSuccess, showError } = useToast()
  const { setCampaignStatus } = useGlobalStoreActions()

  const [nextLoading, setNextLoading] = useState(false)
  const [campaignData, setCampaignData] = useState(null)
  const [progressStatistics, setProgressStatistics] = useState(null)

  const [firms, setFirms] = useState([])
  const [fetching, setFetching] = useState(true)
  const [rowSelection, setRowSelection] = useState({})

  const intervalRef = useRef(null)

  const [sorting, setSorting] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [suitabilityFilters, setSuitabilityFilters] = useState([])

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE)
  const [totalElements, setTotalElements] = useState(DEFAULT_TOTAL_ELEMENTS)
  const [totalPageCount, setTotalPageCount] = useState(DEFAULT_TOTAL_PAGE_COUNT)
  const suitabilityOptions = useMemo(() => [
    { value: 1, label: t('mailTemplate.suitabilityLevel.highlySuitable'), key: 'HIGHLY_SUITABLE' },
    { value: 2, label: t('mailTemplate.suitabilityLevel.moderatelySuitable'), key: 'MODERATELY_SUITABLE' },
    { value: 3, label: t('mailTemplate.suitabilityLevel.minimallySuitable'), key: 'MINIMALLY_SUITABLE' },
    { value: 4, label: t('mailTemplate.suitabilityLevel.notSuitable'), key: 'NOT_SUITABLE' },
    { value: 5, label: t('mailTemplate.suitabilityLevel.cannotAccess'), key: 'CANNOT_ACCESS' }
  ], [t])

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [saveSelectedLoading, setSaveSelectedLoading] = useState(false)
  const [originalRowSelection, setOriginalRowSelection] = useState({})
  const [abortLoading, setAbortLoading] = useState(false)
  const [titleDialogOpen, setTitleDialogOpen] = useState(false)

  const isAnyFirmSelected = useMemo(() => {
    return Object.keys(rowSelection).some(key => rowSelection[key])
  }, [rowSelection])

  const isDisabled = useMemo(() => {
    return campaignData?.status === 'PREPARING'
  }, [campaignData?.status])

  const hasSelectionChanges = useMemo(() => {
    const currentKeys = Object.keys(rowSelection).filter(key => rowSelection[key])
    const originalKeys = Object.keys(originalRowSelection).filter(key => originalRowSelection[key])

    if (currentKeys.length !== originalKeys.length) {
      return true
    }

    return currentKeys.some(key => !originalRowSelection[key]) || originalKeys.some(key => !rowSelection[key])
  }, [rowSelection, originalRowSelection])

  const buildFilters = useCallback(() => {
    const filters = {}

    if (selectedFilter === 'selected') {
      filters.selected = true
    } else if (selectedFilter === 'not-selected') {
      filters.selected = false
    }

    if (suitabilityFilters.length > 0) {
      filters.suitabilityLevels = suitabilityFilters.map(f => f.key).join(',')
    }

    if (debouncedSearchTerm && debouncedSearchTerm.length >= 3) {
      filters.searchKeyword = debouncedSearchTerm
    }

    return filters
  }, [selectedFilter, suitabilityFilters, debouncedSearchTerm])

  const collectSelectionData = useCallback(() => {
    return firms.map((firm) => ({
      id: firm.id,
      selected: Boolean(rowSelection[firm.id])
    }))
  }, [firms, rowSelection])

  const handleSaveSelected = useCallback(async () => {
    setSaveSelectedLoading(true)

    try {
      const campaignFirmList = collectSelectionData()

      const payload = {
        campaignId: queryParams.b2bCampaignId,
        campaignFirmList
      }

      await updateCampaignFirmSelection(payload)

      setOriginalRowSelection({ ...rowSelection })
      setHasUnsavedChanges(false)

      showSuccess(t('mailTemplate.campaignPreparation.saveSelectedSuccess'))
    } catch (error) {
      console.error('Failed to save selected firms:', error)
      showError(t('mailTemplate.campaignPreparation.saveSelectedError'))
    } finally {
      setSaveSelectedLoading(false)
    }
  }, [collectSelectionData, queryParams.b2bCampaignId, rowSelection, showSuccess, showError, t])

  const handleOpenTitleDialog = useCallback(() => {
    if (!isAnyFirmSelected) {
      showError(t('mailTemplate.campaignPreparation.noFirmsSelectedError'))
      return
    }
    setTitleDialogOpen(true)
  }, [isAnyFirmSelected, showError, t])

  const handleCloseTitleDialog = useCallback(() => {
    setTitleDialogOpen(false)
  }, [])

  const onSubmit = useCallback(async (positions) => {
    setNextLoading(true)
    setTitleDialogOpen(false)

    try {
      await approveCampaignFirmSelection(queryParams.b2bCampaignId, positions)

      showSuccess(t('mailTemplate.campaignPreparation.approveSelectionSuccess'))

      if (onCampaignUpdate) {
        await onCampaignUpdate()
      }
    } catch (error) {
      console.error('Failed to submit campaign:', error)
      showError(t('mailTemplate.campaignPreparation.approveSelectionError'))
    } finally {
      setNextLoading(false)
    }
  }, [queryParams.b2bCampaignId, showSuccess, showError, t, onCampaignUpdate])

  const handleRefresh = useCallback((page = currentPage, size = pageSize, filters = null, sortingParams = null) => {
    if (queryParams?.b2bCampaignId) {
      setFetching(true)

      const currentFilters = filters !== null ? filters : buildFilters()
      const currentSorting = sortingParams !== null ? sortingParams : buildSortingParams(sorting)

      const minDelayPromise = new Promise(resolve => setTimeout(resolve, 800))

      const apiPromise = getCampaignFirmsAndState({ b2bCampaignId: queryParams.b2bCampaignId }, page, size, currentFilters, currentSorting)

      Promise.all([apiPromise, minDelayPromise]).then(([res]) => {
        const firmList = Array.isArray(res.data.campaignFirms?.content) ? res.data.campaignFirms.content : []
        const backendSelection = firmList.reduce((acc, firm) => {
          if (firm.selected) {
            acc[firm.id] = true
          }
          return acc
        }, {})

        setFirms(firmList)
        setRowSelection(backendSelection)
        setOriginalRowSelection(backendSelection)
        if (res.data.campaignFirms?.totalRecordCount !== undefined) {
          setTotalElements(res.data.campaignFirms.totalRecordCount)
        }
        if (res.data.campaignFirms?.totalPageCount !== undefined) {
          setTotalPageCount(res.data.campaignFirms.totalPageCount)
        }
        if (res.data.progressStatistics) {
          setProgressStatistics(res.data.progressStatistics)
        }

        if (res.data.campaignStatus) {
          setCampaignData(prev => ({ ...prev, status: res.data.campaignStatus }))
          setCampaignStatus(queryParams.b2bCampaignId, res.data.campaignStatus)
        }
      }).finally(() => {
        setFetching(false)
      })
    }
  }, [queryParams?.b2bCampaignId, setCampaignStatus, currentPage, pageSize, buildFilters, sorting])

  const queryParamsError = useCallback((queryParams, b2bAccountIdError, b2bCampaignIdError) => {
    const b2bAccountId = queryParams?.b2bAccountId
    const b2bCampaignId = queryParams?.b2bCampaignId

    if (b2bAccountId && b2bCampaignId) {
      return false
    } else if (b2bCampaignId) {
      return <div>{b2bAccountIdError}</div>
    } else {
      return <div>{b2bCampaignIdError}</div>
    }
  }, [])

  useEffect(() => {
    if (queryParams?.b2bCampaignId) {
      getCampaign(queryParams.b2bCampaignId).then((campaignRes) => {
        setCampaignData(campaignRes.data)
        if (campaignRes.data?.status) {
          setCampaignStatus(queryParams.b2bCampaignId, campaignRes.data.status)
        }
      }).catch((error) => {
        console.error('Failed to fetch campaign data:', error)
      })

      getCampaignFirmsAndState({ b2bCampaignId: queryParams.b2bCampaignId }, DEFAULT_PAGE, DEFAULT_PAGE_SIZE, {}).then((res) => {
        const firmList = Array.isArray(res.data.campaignFirms?.content) ? res.data.campaignFirms.content : []
        const backendSelection = firmList.reduce((acc, firm) => {
          if (firm.selected) {
            acc[firm.id] = true
          }
          return acc
        }, {})

        setFirms(firmList)
        setRowSelection(backendSelection)
        setOriginalRowSelection(backendSelection)
        if (res.data.campaignFirms?.totalRecordCount !== undefined) {
          setTotalElements(res.data.campaignFirms.totalRecordCount)
        }
        if (res.data.campaignFirms?.totalPageCount !== undefined) {
          setTotalPageCount(res.data.campaignFirms.totalPageCount)
        }
        if (res.data.progressStatistics) {
          setProgressStatistics(res.data.progressStatistics)
        }

        if (res.data.campaignStatus) {
          setCampaignData(prev => ({ ...prev, status: res.data.campaignStatus }))
          setCampaignStatus(queryParams.b2bCampaignId, res.data.campaignStatus)
        }
      }).finally(() => {
        setFetching(false)
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.b2bCampaignId, setCampaignStatus])

  useEffect(() => {
    setHasUnsavedChanges(hasSelectionChanges)
  }, [hasSelectionChanges])

  const handlePageChange = useCallback((newPage) => {
    const filters = buildFilters()
    const sortingParams = buildSortingParams(sorting)

    setCurrentPage(newPage)
    handleRefresh(newPage, pageSize, filters, sortingParams)
  }, [handleRefresh, pageSize, buildFilters, sorting])

  const handlePageSizeChange = useCallback((newSize) => {
    const filters = buildFilters()
    const sortingParams = buildSortingParams(sorting)

    setPageSize(newSize)
    setCurrentPage(DEFAULT_PAGE)

    handleRefresh(DEFAULT_PAGE, newSize, filters, sortingParams)
  }, [handleRefresh, buildFilters, sorting])

  const handleSortingChange = useCallback((newSorting) => {
    const filters = buildFilters()
    const sortingParams = buildSortingParams(newSorting)

    setSorting(newSorting)
    setCurrentPage(DEFAULT_PAGE)

    handleRefresh(DEFAULT_PAGE, pageSize, filters, sortingParams)
  }, [handleRefresh, pageSize, buildFilters])

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.length >= 3 || searchTerm.length === 0) {
        setDebouncedSearchTerm(searchTerm)
      }
    }, 500)

    return () => clearTimeout(handler)
  }, [searchTerm])

  useEffect(() => {
    if (queryParams?.b2bCampaignId && !fetching) {
      const filters = buildFilters()
      const sortingParams = buildSortingParams(sorting)

      setCurrentPage(DEFAULT_PAGE)

      handleRefresh(DEFAULT_PAGE, pageSize, filters, sortingParams)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, suitabilityFilters, debouncedSearchTerm])

  useEffect(() => {
    const shouldPoll = (campaignData?.status === 'PREPARING' || campaignData?.status === 'CONTACTS_SEARCHING') && !fetching

    if (shouldPoll) {
      intervalRef.current = setInterval(() => {
        handleRefresh()
      }, 7500)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [campaignData?.status, fetching, handleRefresh])

  const handleAbort = useCallback(async () => {
    setAbortLoading(true)

    try {
      await abortSelectFirms(queryParams.b2bCampaignId)

      showSuccess(t('mailTemplate.campaignPreparation.abortSuccess'))

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }

      await handleRefresh()

      if (onCampaignUpdate) {
        await onCampaignUpdate()
      }
    } catch (error) {
      console.error('Failed to abort firm selection:', error)
      showError(t('mailTemplate.campaignPreparation.abortError'))
    } finally {
      setAbortLoading(false)
    }
  }, [queryParams.b2bCampaignId, showSuccess, showError, t, handleRefresh, onCampaignUpdate])

  const handleDownloadExcel = async () => {
    try {
      const selected = selectedFilter === 'all'
        ? null
        : selectedFilter === 'selected'

      const suitabilityLevelOrders = suitabilityFilters.length > 0
        ? suitabilityFilters.map(opt => opt.value)
        : null

      const res = await downloadCampaignFirmsExcel(queryParams.b2bCampaignId, selected, suitabilityLevelOrders)
      const blob = new Blob([res.data], { type: 'application/octet-stream' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `campaign-firms-${queryParams.b2bCampaignId}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      // Error handling is done by the B2BAPI interceptor
    }
  }

  return (
    queryParamsError(queryParams, t('mailTemplate.wizard.b2bAccountIdError'), t('mailTemplate.wizard.b2bCampaignIdError')) ||
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="p-2" style={{ direction: 'ltr' }}>
        <div className="flex justify-center items-center mb-4">
          <div className="flex flex-col items-center">
            <Psychology sx={{ fontSize: 50 }} color="primary"/>
            <Typography variant="caption" color="primary">
              {t('mailTemplate.campaignPreparation.aiFiltered')}
            </Typography>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
              variant="contained"
              onClick={handleDownloadExcel}
              sx={{ borderRadius: 2 }}
              endIcon={<TableViewIcon/>}
              disabled={!firms?.length}
            >
              {t('down_xls')}
            </Button>

          <Button
                variant="outlined"
                onClick={handleSaveSelected}
                disabled={saveSelectedLoading || isDisabled || !hasUnsavedChanges}
                startIcon={saveSelectedLoading ? <CircularProgress sx={{ color: 'inherit' }} size={18}/> : null}
                sx={{ borderRadius: 2 }}
              >
                {saveSelectedLoading ? t('mailTemplate.campaignPreparation.savingSelected') : t('mailTemplate.campaignPreparation.saveSelected')}
              </Button>
              <Tooltip
                title={hasUnsavedChanges ? t('mailTemplate.campaignPreparation.saveBeforeStart') : ''}
                arrow
                placement="top"
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleOpenTitleDialog}
                    disabled={nextLoading || isDisabled || hasUnsavedChanges}
                    startIcon={nextLoading ? <CircularProgress sx={{ color: 'inherit' }} size={18}/> : null}
                    sx={{ borderRadius: 2 }}
                  >
                    {nextLoading ? t('mailTemplate.campaignPreparation.approvingSelection') : t('mailTemplate.campaignPreparation.approveSelection')}
                  </Button>
                </span>
              </Tooltip>
        </div>
      </div>

      <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {campaignData?.status === 'PREPARING' && (
            <>
              <IconButton
                onClick={handleRefresh}
                disabled={fetching}
                color="primary"
                sx={{ borderRadius: 2 }}
              >
                {fetching ? <CircularProgress size={24}/> : <Refresh/>}
              </IconButton>
              <Button
                variant="outlined"
                color="error"
                onClick={handleAbort}
                disabled={abortLoading}
                startIcon={abortLoading ? <CircularProgress sx={{ color: 'inherit' }} size={18}/> : null}
                sx={{ borderRadius: 2 }}
              >
                {abortLoading ? t('mailTemplate.campaignPreparation.aborting') : t('mailTemplate.campaignPreparation.abort')}
              </Button>
            </>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={selectedFilter}
            exclusive
            onChange={(_, newValue) => newValue && setSelectedFilter(newValue)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                py: 0.75,
                px: 2,
                fontSize: '13px',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark'
                  }
                }
              }
            }}
          >
            <ToggleButton value="all">
              {t('mailTemplate.campaignReview.all')}
            </ToggleButton>
            <ToggleButton value="selected">
              {t('mailTemplate.campaignReview.selected')}
            </ToggleButton>
            <ToggleButton value="not-selected">
              {t('mailTemplate.campaignReview.notSelected')}
            </ToggleButton>
          </ToggleButtonGroup>

          <Autocomplete
            multiple
            size="small"
            limitTags={2}
            options={suitabilityOptions}
            disableCloseOnSelect
            openOnFocus
            getOptionLabel={(option) => option.label}
            value={suitabilityFilters}
            onChange={(_, newValue) => setSuitabilityFilters(newValue)}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <MuiCheckbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={suitabilityFilters.length === 0 ? t('mailTemplate.campaignPreparation.filter.suitabilityPlaceholder') : ''}
              />
            )}
            slotProps={{
              chip: {
                clickable: true
              }
            }}
            sx={{
              width: 280,
              '& .MuiAutocomplete-inputRoot': {
                flexWrap: 'nowrap',
                overflow: 'hidden',
                cursor: 'pointer'
              },
              '& .MuiAutocomplete-input': {
                minWidth: '30px !important'
              },
              '& .MuiAutocomplete-tag': {
                maxWidth: 100
              }
            }}
          />

          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('mailTemplate.campaigns.search')}
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search/>
                </InputAdornment>
              )
            }}
          />
          <FormControl sx={{ minWidth: 80 }}>
            <Select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              size="small"
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {showProgressBar && (
        <CampaignProgressBar
          campaign={campaignData || campaign}
          progressStatistics={progressStatistics}
        />
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', marginTop: '10px' }}>
        <CampaignPreparationTable
          data={firms}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          fetching={fetching}
          fuzzyFilter={fuzzyFilter}
          globalFilter={searchTerm}
          setGlobalFilter={setSearchTerm}
          pageSize={pageSize}
          currentPage={currentPage}
          totalElements={totalElements}
          totalPageCount={totalPageCount}
          onPageChange={handlePageChange}
          disableInteraction={isDisabled}
          hideCheckboxes={false}
          sorting={sorting}
          onSortingChange={handleSortingChange}
        />
      </Box>

      <TitleFilterDialog
        open={titleDialogOpen}
        onConfirm={onSubmit}
        onCancel={handleCloseTitleDialog}
        loading={nextLoading}
      />
    </Box>
  )
}

export default CampaignPreparation
